import React, { useCallback, useMemo, type ReactNode } from "react"

import { Button } from "@coval/ui"
import { Plus } from "lucide-react"

import {
  SimulationWrapper,
  SimulationWrapperType,
  type SimulationWrapperConfig,
} from "../types"
import { ApiWrapperTopLevelConfigForm } from "./ApiWrapperTopLevelConfigForm"

interface ApiSimulationWrapperConfigFormProps {
  config: SimulationWrapperConfig | null | undefined
  onChange: (config: SimulationWrapperConfig) => void
}

const ApiSimulationWrapperConfigForm: React.FC<
  ApiSimulationWrapperConfigFormProps
> = ({ config, onChange }) => {
  const currentConfig = useMemo(() => config || {}, [config])

  const updateConfig = useCallback(
    (updates: Partial<SimulationWrapperConfig>) => {
      onChange({ ...currentConfig, ...updates })
    },
    [currentConfig, onChange],
  )

  const handleAddPreSimulation = () => {
    updateConfig({
      pre_simulation: [
        {
          api_url: "",
          method: "GET",
          request_body: null,
          headers: null,
          timeout: 30,
        },
      ],
    })
  }

  const preSimulationCalls = currentConfig.pre_simulation ?? []
  const preSimulationConfig = preSimulationCalls[0]
  const remainingPreSimulationCalls = preSimulationCalls.slice(1)

  return (
    <div className="space-y-4">
      {preSimulationConfig ? (
        <ApiWrapperTopLevelConfigForm
          label="Pre-Simulation API Call"
          config={preSimulationConfig}
          onChange={(newConfig) =>
            updateConfig({
              pre_simulation: newConfig
                ? [newConfig, ...remainingPreSimulationCalls]
                : remainingPreSimulationCalls,
            })
          }
          onRemove={() =>
            updateConfig({ pre_simulation: remainingPreSimulationCalls })
          }
        />
      ) : (
        <Button
          variant="secondary"
          onClick={handleAddPreSimulation}
          className="w-full"
        >
          <Plus className="w-4 h-4" />
          Add Pre-Simulation API Call
        </Button>
      )}
    </div>
  )
}

export class ApiSimulationWrapper extends SimulationWrapper {
  constructor() {
    super({
      type: SimulationWrapperType.API_WRAPPER,
      name: "Api",
      display_name: "API",
      metadata: {},
    })
  }

  render(): ReactNode {
    return (
      <div className="text-sm">
        <span className="font-medium">API Wrapper</span>
      </div>
    )
  }

  renderTopLevelConfig(
    config: SimulationWrapperConfig | null | undefined,
    onConfigChange: (config: SimulationWrapperConfig) => void,
  ): ReactNode {
    return (
      <ApiSimulationWrapperConfigForm
        config={config}
        onChange={onConfigChange}
      />
    )
  }
}
